"use client"

import * as React from "react"
import { createContext, useContext, useState, useCallback, useMemo, type ReactNode } from "react"
import { useInRouterContext, useParams } from "react-router-dom"
import { useURLState } from "@/hooks/useURLState"
import { usePropertySafe } from "@/lib/contexts/PropertyContext"
import { useWorkspaceSafe } from "@/lib/contexts/WorkspaceContext"
import { parseSlugId } from "@/lib/routing/slug-id"

// ============================================================================
// Types
// ============================================================================

interface PageMetrics {
  // General metrics
  sessions?: number
  pageViews?: number
  bounceRate?: number
  avgSessionDuration?: number
  
  // Traffic metrics
  topSources?: Array<{ name: string; count: number }>
  topPages?: Array<{ page: string; views: number }>
  
  // Device metrics
  deviceBreakdown?: Array<{ device: string; percentage: number }>
  browserBreakdown?: Array<{ browser: string; percentage: number }>
  
  // Geographic metrics
  topCountries?: Array<{ country: string; visitors: number }>
  
  // Conversion metrics
  conversionRate?: number
  formSubmissions?: number
  clicks?: number
  
  // Custom data that components can add
  custom?: Record<string, any>
}

// ============================================================================
// Structured AI Context Types (Explicit Naming)
// ============================================================================

/**
 * Identifies where the user is in the Humblytics app
 */
interface AppExperience {
  /** Route identifier - matches route structure (e.g., "split_test_editor", "traffic_dashboard") */
  routeId: string
  /** Human-readable name shown in UI */
  displayName: string
}

/**
 * Context specific to the split test editor experience
 */
interface SplitTestEditorContext {
  /** Editor mode type */
  mode: "nocode_visual_editor"
  /** The external page URL being edited (e.g., https://example.com/landing) */
  targetPageUrl: string
  /** Draft/test ID for the current experiment */
  draftId: string
  /** Currently selected DOM element in the editor */
  selectedElement?: {
    selector: string
    tagName: string
    textContent: string
  }
  /** Instructions already applied in this session */
  appliedInstructions?: Array<{
    selector: string
    operation: string
    description?: string
  }>
  /** Page structure summary for AI context - key elements with their selectors */
  pageStructure?: PageStructureSummary
}

/** Summary of key page elements for AI context */
interface PageStructureSummary {
  title: string
  headings: Array<{
    text: string
    level: number
    selector: string
  }>
  paragraphs?: Array<{
    text: string
    selector: string
  }>
  buttons: Array<{
    text: string
    selector: string
  }>
  links: Array<{
    text: string
    href: string
    selector: string
  }>
  images: Array<{
    alt: string
    src: string
    selector: string
  }>
  forms: Array<{
    selector: string
    fields: number
  }>
}

/**
 * Capabilities available in the current context
 * Different experiences enable different AI capabilities
 */
interface AICapabilities {
  /** Can the AI apply visual changes to a page? */
  canApplyInstructions: boolean
  /** Callback to apply a component instruction (if available) */
  onApplyInstruction?: (instruction: any) => void
  /** Override label for the apply button in ComponentInstructionRenderer */
  applyButtonLabel?: string
}

interface AIPageContext {
  // Current page information (legacy - app route path)
  currentPage: string
  pageTitle: string
  
  // NEW: Structured experience context
  appExperience?: AppExperience
  
  // NEW: Experience-specific context (only one should be set)
  splitTestEditorContext?: SplitTestEditorContext
  
  // NEW: Available AI capabilities
  capabilities?: AICapabilities
  
  // Filter state
  period: string
  filters: Array<{ field: string; operator: string; value: string }>
  
  // Property/site context
  propertyId: string | null
  propertyName: string | null
  domain: string | null
  
  // Current metrics visible on the page
  metrics: PageMetrics
  
  // Dashboard data snapshot (sanitized for AI)
  dashboardSnapshot: any | null
}

interface AIPageContextValue {
  // Current context
  context: AIPageContext
  
  // Methods to update context
  setMetrics: (metrics: Partial<PageMetrics>) => void
  setCustomData: (key: string, value: any) => void
  setDashboardSnapshot: (data: any) => void
  setCapabilities: (capabilities: AICapabilities) => void
  setSplitTestEditorContext: (context: Partial<SplitTestEditorContext>) => void
  clearContext: () => void
  
  // Get formatted context for AI prompts
  getContextForAI: () => string
  getContextObject: () => AIPageContext
}

// ============================================================================
// Context
// ============================================================================

const AIPageContextContext = createContext<AIPageContextValue | undefined>(undefined)

// ============================================================================
// Helper Functions
// ============================================================================

function getPageTitle(pathname: string): string {
  const segments = pathname.split('/').filter(Boolean)
  const lastSegment = segments[segments.length - 1] || 'dashboard'
  
  const titles: Record<string, string> = {
    'traffic': 'Traffic Analytics',
    'pages': 'Pages Analytics',
    'devices': 'Devices & Browsers',
    'locations': 'Geographic Analytics',
    'channels': 'Traffic Channels',
    'clicks': 'Click Analytics',
    'forms': 'Form Analytics',
    'heatmaps': 'Heatmaps',
    'funnel-analyzer': 'Funnel Analysis',
    'split-testing': 'A/B Testing',
    'split-tests': 'A/B Testing',
    'experiments': 'Experiments',
    'workflows': 'Automation Workflows',
    'attribution': 'Ads Attribution',
    'connectors': 'Data Connectors',
    'ai-chat': 'AI Assistant',
    'llm-referrals': 'LLM Referrals',
  }
  
  return titles[lastSegment] || 'Dashboard'
}

function formatPeriodLabel(period: string): string {
  if (period.includes(':')) {
    // Custom date range
    const [start, end] = period.split(':')
    return `${start} to ${end}`
  }
  
  const labels: Record<string, string> = {
    '1d': 'Last 24 hours',
    '7d': 'Last 7 days',
    '30d': 'Last 30 days',
    '90d': 'Last 90 days',
    '12m': 'Last 12 months',
    'mtd': 'Month to date',
    'ytd': 'Year to date',
  }
  
  return labels[period] || period
}

// ============================================================================
// Provider Component
// ============================================================================

interface AIPageContextProviderProps {
  children: ReactNode
  dashboardData?: any
  /**
   * Optional pre-built context value. When provided, the provider will use this
   * instead of deriving context from useLocation() and other router-dependent hooks.
   * This is useful for direct mode (outside of React Router context).
   */
  value?: {
    context: Partial<AIPageContext>
  }
}

/**
 * Inner provider that uses router-dependent hooks.
 * Only rendered when in router context and no value is provided.
 */
function AIPageContextProviderWithRouter({ 
  children, 
  dashboardData 
}: { 
  children: ReactNode
  dashboardData?: any 
}) {
  const urlStateResult = useURLState()
  const propertyContext = usePropertySafe()
  const workspaceContext = useWorkspaceSafe()
  
  // Get URL params to extract propertyId as fallback
  const { propertySlugId } = useParams<{ propertySlugId?: string }>()
  
  // Local state for metrics and custom data
  const [metrics, setMetricsState] = useState<PageMetrics>({})
  const [dashboardSnapshot, setDashboardSnapshotState] = useState<any | null>(null)
  
  const pathname = window.location.pathname
  const currentPage = pathname
  const pageTitle = getPageTitle(pathname)
  const period = urlStateResult.state?.period ?? '7d'
  const filters = urlStateResult.state?.filters ?? []
  
  // Get propertyId from contexts, or parse from URL as fallback
  // This ensures propertyId is available even before async data loads
  const propertyIdFromUrl = propertySlugId ? parseSlugId(propertySlugId).id : null
  const propertyId = workspaceContext?.propertyId ?? propertyContext?.property?.propertyId ?? propertyIdFromUrl
  const propertyName = propertyContext?.property?.name ?? workspaceContext?.workspace?.name ?? null
  const domain = propertyContext?.property?.domain ?? null

  // Get last segment for route matching
  const lastSegment = pathname.split('/').filter(Boolean).pop() || 'dashboard'
  
  // Derive appExperience from current route
  const appExperience: AppExperience = useMemo(() => ({
    routeId: lastSegment,
    displayName: pageTitle,
  }), [lastSegment, pageTitle])

  return (
    <AIPageContextProviderInner
      currentPage={currentPage}
      pageTitle={pageTitle}
      period={period}
      filters={filters}
      propertyId={propertyId}
      propertyName={propertyName}
      domain={domain}
      metrics={metrics}
      setMetricsState={setMetricsState}
      dashboardSnapshot={dashboardSnapshot}
      setDashboardSnapshotState={setDashboardSnapshotState}
      dashboardData={dashboardData}
      appExperience={appExperience}
    >
      {children}
    </AIPageContextProviderInner>
  )
}

/**
 * Inner provider that uses provided values (no router dependency).
 * Used when a value prop is provided or when outside router context.
 */
function AIPageContextProviderStandalone({ 
  children, 
  dashboardData,
  providedContext
}: { 
  children: ReactNode
  dashboardData?: any
  providedContext?: Partial<AIPageContext>
}) {
  const propertyContext = usePropertySafe()
  const workspaceContext = useWorkspaceSafe()
  
  // Local state for metrics and custom data
  const [metrics, setMetricsState] = useState<PageMetrics>({})
  const [dashboardSnapshot, setDashboardSnapshotState] = useState<any | null>(null)
  
  const currentPage = providedContext?.currentPage ?? window.location.pathname
  const pageTitle = providedContext?.pageTitle ?? getPageTitle(currentPage)
  const period = providedContext?.period ?? '7d'
  const filters = providedContext?.filters ?? []
  const propertyId = providedContext?.propertyId ?? workspaceContext?.propertyId ?? propertyContext?.property?.propertyId ?? null
  const propertyName = providedContext?.propertyName ?? propertyContext?.property?.name ?? workspaceContext?.workspace?.name ?? null
  const domain = providedContext?.domain ?? propertyContext?.property?.domain ?? null
  
  // New structured context fields
  const appExperience = providedContext?.appExperience
  const splitTestEditorContext = providedContext?.splitTestEditorContext
  const capabilities = providedContext?.capabilities

  return (
    <AIPageContextProviderInner
      currentPage={currentPage}
      pageTitle={pageTitle}
      period={period}
      filters={filters}
      propertyId={propertyId}
      propertyName={propertyName}
      domain={domain}
      metrics={metrics}
      setMetricsState={setMetricsState}
      dashboardSnapshot={dashboardSnapshot}
      setDashboardSnapshotState={setDashboardSnapshotState}
      dashboardData={dashboardData}
      appExperience={appExperience}
      splitTestEditorContext={splitTestEditorContext}
      capabilities={capabilities}
    >
      {children}
    </AIPageContextProviderInner>
  )
}

/**
 * Shared inner component with context creation logic
 */
function AIPageContextProviderInner({
  children,
  currentPage,
  pageTitle,
  period,
  filters,
  propertyId,
  propertyName,
  domain,
  metrics,
  setMetricsState,
  dashboardSnapshot,
  setDashboardSnapshotState,
  dashboardData,
  // New structured context fields (initial values from props)
  appExperience: initialAppExperience,
  splitTestEditorContext: initialSplitTestEditorContext,
  capabilities: initialCapabilities,
}: {
  children: ReactNode
  currentPage: string
  pageTitle: string
  period: string
  filters: Array<{ field: string; operator: string; value: string }>
  propertyId: string | null
  propertyName: string | null
  domain: string | null
  metrics: PageMetrics
  setMetricsState: React.Dispatch<React.SetStateAction<PageMetrics>>
  dashboardSnapshot: any | null
  setDashboardSnapshotState: React.Dispatch<React.SetStateAction<any | null>>
  dashboardData?: any
  appExperience?: AppExperience
  splitTestEditorContext?: SplitTestEditorContext
  capabilities?: AICapabilities
}) {
  // Access workspace context for connector info
  const workspaceCtx = useWorkspaceSafe()

  // State for dynamically updatable context fields
  const [capabilitiesState, setCapabilitiesState] = useState<AICapabilities | undefined>(initialCapabilities)
  const [splitTestEditorContextState, setSplitTestEditorContextState] = useState<SplitTestEditorContext | undefined>(initialSplitTestEditorContext)
  
  // Context object
  const context = useMemo<AIPageContext>(() => ({
    currentPage,
    pageTitle,
    period,
    filters,
    propertyId,
    propertyName,
    domain,
    metrics,
    dashboardSnapshot,
    // New structured fields (use state versions so they can be updated)
    appExperience: initialAppExperience,
    splitTestEditorContext: splitTestEditorContextState,
    capabilities: capabilitiesState,
  }), [currentPage, pageTitle, period, filters, propertyId, propertyName, domain, metrics, dashboardSnapshot, initialAppExperience, splitTestEditorContextState, capabilitiesState])
  
  // Update methods
  const setMetrics = useCallback((newMetrics: Partial<PageMetrics>) => {
    setMetricsState(prev => ({ ...prev, ...newMetrics }))
  }, [setMetricsState])
  
  const setCustomData = useCallback((key: string, value: any) => {
    setMetricsState(prev => ({
      ...prev,
      custom: { ...prev.custom, [key]: value }
    }))
  }, [setMetricsState])
  
  const setDashboardSnapshot = useCallback((data: any) => {
    setDashboardSnapshotState(data)
  }, [setDashboardSnapshotState])
  
  const setCapabilities = useCallback((newCapabilities: AICapabilities) => {
    setCapabilitiesState(newCapabilities)
  }, [])
  
  const setSplitTestEditorContext = useCallback((updates: Partial<SplitTestEditorContext>) => {
    setSplitTestEditorContextState(prev => {
      // If previous exists, merge with updates
      if (prev) {
        return { ...prev, ...updates }
      }
      // If no previous context, create a new one with required fields
      // Only create if we have the required fields
      if (updates.mode && updates.targetPageUrl && updates.draftId) {
        return {
          mode: updates.mode,
          targetPageUrl: updates.targetPageUrl,
          draftId: updates.draftId,
          selectedElement: updates.selectedElement,
          appliedInstructions: updates.appliedInstructions,
          pageStructure: updates.pageStructure,
        }
      }
      // Otherwise return undefined (can't create context without required fields)
      return undefined
    })
  }, [])
  
  const clearContext = useCallback(() => {
    setMetricsState({})
    setDashboardSnapshotState(null)
    setCapabilitiesState(undefined)
    setSplitTestEditorContextState(undefined)
  }, [setMetricsState, setDashboardSnapshotState])
  
  // Format context for AI prompts
  const getContextForAI = useCallback((): string => {
    const lines: string[] = []
    
    lines.push(`## Current Page Context`)
    lines.push(`- **Page**: ${pageTitle}`)
    lines.push(`- **Time Period**: ${formatPeriodLabel(period)}`)
    
    if (propertyName) {
      lines.push(`- **Property**: ${propertyName}`)
    }
    if (domain) {
      lines.push(`- **Domain**: ${domain}`)
    }
    
    if (filters.length > 0) {
      lines.push(`- **Active Filters**: ${filters.map(f => `${f.field}${f.operator}${f.value}`).join(', ')}`)
    }
    
    // Add split test editor specific context
    if (context.splitTestEditorContext) {
      const editorCtx = context.splitTestEditorContext
      lines.push(`\n## Split Test Editor Context`)
      lines.push(`- **Mode**: No-code Visual Editor`)
      lines.push(`- **Target Page URL**: ${editorCtx.targetPageUrl}`)
      lines.push(`- **Draft ID**: ${editorCtx.draftId}`)
      
      if (editorCtx.selectedElement) {
        lines.push(`- **Selected Element**: <${editorCtx.selectedElement.tagName}> "${editorCtx.selectedElement.textContent?.substring(0, 50) || ''}"`)
        lines.push(`- **Element Selector**: ${editorCtx.selectedElement.selector}`)
      }
      
      if (editorCtx.appliedInstructions && editorCtx.appliedInstructions.length > 0) {
        lines.push(`\n### Applied Changes (${editorCtx.appliedInstructions.length})`)
        editorCtx.appliedInstructions.slice(0, 5).forEach((inst, i) => {
          lines.push(`${i + 1}. ${inst.operation} on ${inst.selector}${inst.description ? `: ${inst.description}` : ''}`)
        })
      }
      
      // Add page structure for AI to understand available elements and their selectors
      if (editorCtx.pageStructure) {
        const ps = editorCtx.pageStructure
        lines.push(`\n### Page Structure (Use these selectors to target elements)`)
        lines.push(`**Page Title**: ${ps.title}`)
        
        if (ps.headings.length > 0) {
          lines.push(`\n**Headings:**`)
          ps.headings.forEach(h => {
            lines.push(`- H${h.level}: "${h.text}" → selector: \`${h.selector}\``)
          })
        }
        
        if (ps.paragraphs && ps.paragraphs.length > 0) {
          lines.push(`\n**Paragraphs:**`)
          ps.paragraphs.slice(0, 10).forEach(p => {
            lines.push(`- "${p.text}" → selector: \`${p.selector}\``)
          })
        }
        
        if (ps.buttons.length > 0) {
          lines.push(`\n**Buttons/CTAs:**`)
          ps.buttons.slice(0, 10).forEach(b => {
            lines.push(`- "${b.text}" → selector: \`${b.selector}\``)
          })
        }
        
        if (ps.images.length > 0) {
          lines.push(`\n**Images:**`)
          ps.images.slice(0, 5).forEach(img => {
            lines.push(`- ${img.alt || '(no alt)'} → selector: \`${img.selector}\``)
          })
        }
        
        if (ps.forms.length > 0) {
          lines.push(`\n**Forms:**`)
          ps.forms.forEach(f => {
            lines.push(`- Form with ${f.fields} fields → selector: \`${f.selector}\``)
          })
        }
      }
    }
    
    // Add key metrics if available
    if (Object.keys(metrics).length > 0) {
      lines.push(`\n## Key Metrics`)
      
      if (metrics.sessions !== undefined) {
        lines.push(`- **Sessions**: ${metrics.sessions.toLocaleString()}`)
      }
      if (metrics.pageViews !== undefined) {
        lines.push(`- **Page Views**: ${metrics.pageViews.toLocaleString()}`)
      }
      if (metrics.bounceRate !== undefined) {
        lines.push(`- **Bounce Rate**: ${metrics.bounceRate.toFixed(1)}%`)
      }
      if (metrics.avgSessionDuration !== undefined) {
        const mins = Math.floor(metrics.avgSessionDuration / 60)
        const secs = Math.round(metrics.avgSessionDuration % 60)
        lines.push(`- **Avg. Session Duration**: ${mins}m ${secs}s`)
      }
      if (metrics.conversionRate !== undefined) {
        lines.push(`- **Conversion Rate**: ${metrics.conversionRate.toFixed(2)}%`)
      }
      if (metrics.formSubmissions !== undefined) {
        lines.push(`- **Form Submissions**: ${metrics.formSubmissions.toLocaleString()}`)
      }
      if (metrics.clicks !== undefined) {
        lines.push(`- **Clicks**: ${metrics.clicks.toLocaleString()}`)
      }
      
      if (metrics.topSources && metrics.topSources.length > 0) {
        lines.push(`\n### Top Traffic Sources`)
        metrics.topSources.slice(0, 5).forEach((source, i) => {
          lines.push(`${i + 1}. ${source.name}: ${source.count.toLocaleString()}`)
        })
      }
      
      if (metrics.topPages && metrics.topPages.length > 0) {
        lines.push(`\n### Top Pages`)
        metrics.topPages.slice(0, 5).forEach((page, i) => {
          lines.push(`${i + 1}. ${page.page}: ${page.views.toLocaleString()} views`)
        })
      }
      
      if (metrics.topCountries && metrics.topCountries.length > 0) {
        lines.push(`\n### Top Countries`)
        metrics.topCountries.slice(0, 5).forEach((country, i) => {
          lines.push(`${i + 1}. ${country.country}: ${country.visitors.toLocaleString()} visitors`)
        })
      }

      // Add connected data sources / connectors
      if (workspaceCtx?.connectors && workspaceCtx.connectors.length > 0) {
        const connectedSources = workspaceCtx.connectors.filter(c => c.status === 'connected')
        if (connectedSources.length > 0) {
          lines.push(`\n### Connected Data Sources`)
          connectedSources.forEach(c => {
            const typeName = c.type === 'ga4' ? 'Google Analytics 4'
              : c.type === 'meta' ? 'Meta/Facebook Ads'
              : c.type === 'stripe' ? 'Stripe (Revenue)'
              : c.type === 'foxy' ? 'Foxy (Revenue)'
              : c.type === 'humblytics' ? 'Humblytics Tracking'
              : c.name || c.type
            lines.push(`- **${typeName}**: Connected${c.lastSyncAt ? ` (last sync: ${new Date(c.lastSyncAt).toLocaleDateString()})` : ''}`)
          })
        }
      }

      // Serialize custom page-specific data
      if (metrics.custom && Object.keys(metrics.custom).length > 0) {
        for (const [key, value] of Object.entries(metrics.custom)) {
          if (value == null) continue
          if (typeof value === 'string') {
            lines.push(`\n### ${key}`)
            lines.push(value)
          } else {
            lines.push(`\n### ${key}`)
            lines.push(`\`\`\`json\n${JSON.stringify(value, null, 2).slice(0, 4000)}\n\`\`\``)
          }
        }
      }
    }

    return lines.join('\n')
  }, [pageTitle, period, propertyName, domain, filters, metrics, context.splitTestEditorContext, workspaceCtx?.connectors])
  
  const getContextObject = useCallback((): AIPageContext => context, [context])
  
  // Effect to update context when dashboard data changes.
  // Field names must match TrafficMetricsCard / bridge adapters:
  // sessions trends use `visitors` (not `count`); summary KPIs live on stats.*.
  React.useEffect(() => {
    if (dashboardData) {
      setDashboardSnapshotState(dashboardData)
      
      const stats = dashboardData?.stats?.metrics || dashboardData?.metrics || {}
      const trends = stats?.trends || {}
      
      const extractedMetrics: PageMetrics = {}

      const summarySessions =
        Number(stats?.totalSessions ?? trends?.totalSessions?.[0]?.total_sessions ?? 0) || 0
      const summaryViews = Number(stats?.pageViews ?? 0) || 0
      const summaryBounce = stats?.bounceRate != null ? Number(stats.bounceRate) : null

      // Sessions / visitors — prefer summary, else sum trend buckets
      if (summarySessions > 0) {
        extractedMetrics.sessions = summarySessions
      } else if (Array.isArray(trends?.sessions) && trends.sessions.length > 0) {
        extractedMetrics.sessions = trends.sessions.reduce(
          (sum: number, s: any) =>
            sum + (parseInt(String(s.visitors ?? s.count ?? '0'), 10) || 0),
          0
        )
      }

      // Page views — prefer summary, else sum trend buckets
      if (summaryViews > 0) {
        extractedMetrics.pageViews = summaryViews
      } else if (Array.isArray(trends?.views) && trends.views.length > 0) {
        extractedMetrics.pageViews = trends.views.reduce(
          (sum: number, v: any) => sum + (parseInt(String(v.count ?? '0'), 10) || 0),
          0
        )
      }

      // Bounce rate (%) — match TrafficMetricsCard: bounce_count / total_sessions
      const bounceCount = Number(trends?.bounces?.[0]?.bounce_count ?? 0) || 0
      const bounceDenom =
        summarySessions ||
        Number(trends?.totalSessions?.[0]?.total_sessions ?? 0) ||
        0
      if (bounceCount > 0 && bounceDenom > 0) {
        extractedMetrics.bounceRate = (bounceCount / bounceDenom) * 100
      } else if (summaryBounce != null && !Number.isNaN(summaryBounce)) {
        // Bridge summary is typically 0–100; normalize fractions just in case
        extractedMetrics.bounceRate =
          summaryBounce > 0 && summaryBounce <= 1
            ? summaryBounce * 100
            : summaryBounce
      }

      // Avg session duration (seconds)
      if (stats?.avgSessionDuration != null) {
        const raw = Number(stats.avgSessionDuration)
        // Bridge may send ms; TrafficMetricsCard derives from total_session_time_ms
        extractedMetrics.avgSessionDuration =
          raw > 1000 ? raw / 1000 : raw
      } else if (bounceDenom > 0 && Array.isArray(trends?.sessionTime)) {
        const totalMs = trends.sessionTime.reduce(
          (acc: number, s: any) =>
            acc + (parseInt(String(s?.total_session_time ?? '0'), 10) || 0),
          0
        )
        if (totalMs > 0) {
          extractedMetrics.avgSessionDuration = totalMs / (1000 * bounceDenom)
        }
      }
      
      // Top sources
      if (stats?.referrals) {
        extractedMetrics.topSources = stats.referrals.slice(0, 5).map((r: any) => ({
          name: r.referer_group || 'Direct',
          count: parseInt(r.count || '0')
        }))
      }
      
      // Top pages
      if (stats?.pages?.pageViews) {
        extractedMetrics.topPages = stats.pages.pageViews.slice(0, 5).map((p: any) => ({
          page: p.page,
          views: parseInt(p.count || '0')
        }))
      }
      
      // Form submissions
      if (stats?.topMetrics?.formSubmissions) {
        extractedMetrics.formSubmissions = parseInt(stats.topMetrics.formSubmissions[0]?.count || '0')
      }
      
      // Clicks
      if (stats?.topMetrics?.clicks) {
        extractedMetrics.clicks = parseInt(stats.topMetrics.clicks[0]?.count || '0')
      }

      // Conversion rate
      if (stats?.topMetrics?.conversionRate) {
        extractedMetrics.conversionRate = parseFloat(stats.topMetrics.conversionRate[0]?.rate || '0')
      }

      // Revenue data
      const revenue = dashboardData?.revenue || stats?.revenue
      if (revenue) {
        extractedMetrics.custom = {
          ...extractedMetrics.custom,
          'Revenue Summary': [
            revenue.totalRevenue != null ? `Total Revenue: $${Number(revenue.totalRevenue).toLocaleString()}` : null,
            revenue.paidRevenue != null ? `Paid Revenue: $${Number(revenue.paidRevenue).toLocaleString()}` : null,
            revenue.organicRevenue != null ? `Organic Revenue: $${Number(revenue.organicRevenue).toLocaleString()}` : null,
            revenue.revenuePerVisitor != null ? `Revenue per Visitor: $${Number(revenue.revenuePerVisitor).toFixed(2)}` : null,
          ].filter(Boolean),
        }
      }

      setMetricsState(prev => ({ ...prev, ...extractedMetrics }))
    }
  }, [dashboardData])
  
  const value = useMemo<AIPageContextValue>(() => ({
    context,
    setMetrics,
    setCustomData,
    setDashboardSnapshot,
    setCapabilities,
    setSplitTestEditorContext,
    clearContext,
    getContextForAI,
    getContextObject,
  }), [context, setMetrics, setCustomData, setDashboardSnapshot, setCapabilities, setSplitTestEditorContext, clearContext, getContextForAI, getContextObject])
  
  return (
    <AIPageContextContext.Provider value={value}>
      {children}
    </AIPageContextContext.Provider>
  )
}

/**
 * Main AIPageContextProvider component.
 * Automatically chooses between router-dependent and standalone implementations.
 */
export function AIPageContextProvider({ children, dashboardData, value: providedValue }: AIPageContextProviderProps) {
  const isInRouter = useInRouterContext()
  
  // If a value is provided, use standalone mode (no router dependency)
  if (providedValue) {
    return (
      <AIPageContextProviderStandalone 
        dashboardData={dashboardData}
        providedContext={providedValue.context}
      >
        {children}
      </AIPageContextProviderStandalone>
    )
  }
  
  // If in router context, use router-dependent implementation
  if (isInRouter) {
    return (
      <AIPageContextProviderWithRouter dashboardData={dashboardData}>
        {children}
      </AIPageContextProviderWithRouter>
    )
  }
  
  // Fallback: use standalone mode with no provided context
  return (
    <AIPageContextProviderStandalone dashboardData={dashboardData}>
      {children}
    </AIPageContextProviderStandalone>
  )
}

// ============================================================================
// Hook
// ============================================================================

function useAIPageContext() {
  const context = useContext(AIPageContextContext)
  if (!context) {
    throw new Error('useAIPageContext must be used within AIPageContextProvider')
  }
  return context
}

// Safe version that doesn't throw
export function useAIPageContextSafe() {
  return useContext(AIPageContextContext)
}


